import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import EmptyState from "@/components/base/EmptyState";
import StatCard from "@/pages/proprietaire/components/StatCard";
import WhatsappButton from "@/components/base/WhatsappButton";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_CONTRAT_STATUT_LABELS,
  AGENCE_CONTRAT_STATUT_TONES,
  agenceContratReference,
  agenceLocataireInitiales,
  agenceLocataireNomComplet,
} from "@/pages/agence/data/types";
import { formatDate, formatFcfa } from "@/utils/format";
import { useProprietaires } from "../ProprietairesProvider";
import { proprietaireNomComplet } from "../types";
import ProprietaireForm from "../components/ProprietaireForm";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";

interface AccessModule {
  label: string;
  to: string;
  icon: string;
  hint: string;
}

export default function ProprietaireDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getProprietaire, removeProprietaire } = useProprietaires();
  const { getSummary, getImmeuble, getBien, immeublesByProprietaire } = useAgenceData();
  const { can } = useEmployes();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [toDelete, setToDelete] = useState(false);
  const proprietaire = id ? getProprietaire(id) : undefined;

  if (!proprietaire) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/agence/proprietaires"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux propriétaires
        </Link>
        <EmptyState
          icon="ri-user-star-line"
          title="Propriétaire introuvable"
          description="Ce propriétaire n'existe pas ou n'est plus disponible. Retournez à la liste pour le retrouver."
          action={
            <Link
              to="/agence/proprietaires"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les propriétaires
            </Link>
          }
        />
      </div>
    );
  }

  const summary = getSummary(proprietaire.id);
  const actif = proprietaire.statut === "actif";
  const initials = `${proprietaire.prenom.charAt(0)}${proprietaire.nom.charAt(0)}`.toUpperCase();

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Téléphone", value: proprietaire.telephone || "—", icon: "ri-phone-line" },
    { label: "WhatsApp", value: proprietaire.whatsapp || "Non renseigné", icon: "ri-whatsapp-line" },
    { label: "Email", value: proprietaire.email || "—", icon: "ri-mail-line" },
    { label: "Adresse", value: proprietaire.adresse || "—", icon: "ri-map-pin-2-line" },
    { label: "Ajouté le", value: formatDate(proprietaire.createdAt), icon: "ri-calendar-line" },
  ];

  const accessModules: AccessModule[] = [
    {
      label: "Immeubles",
      to: `/agence/immeubles?proprietaire=${proprietaire.id}`,
      icon: "ri-building-2-line",
      hint: "Immeubles rattachés",
    },
    {
      label: "Biens",
      to: `/agence/biens?proprietaire=${proprietaire.id}`,
      icon: "ri-home-4-line",
      hint: "Appartements, boutiques...",
    },
    {
      label: "Locataires",
      to: `/agence/locataires?proprietaire=${proprietaire.id}`,
      icon: "ri-user-3-line",
      hint: "Fiches des occupants",
    },
    {
      label: "Contrats",
      to: `/agence/contrats?proprietaire=${proprietaire.id}`,
      icon: "ri-file-text-line",
      hint: "Baux et échéances",
    },
    {
      label: "Loyers",
      to: `/agence/loyers?proprietaire=${proprietaire.id}`,
      icon: "ri-wallet-3-line",
      hint: "Suivi par période",
    },
    {
      label: "Paiements",
      to: `/agence/paiements?proprietaire=${proprietaire.id}`,
      icon: "ri-hand-coin-line",
      hint: "Encaissements",
    },
    {
      label: "Impayés",
      to: `/agence/impayes?proprietaire=${proprietaire.id}`,
      icon: "ri-alert-line",
      hint: "Loyers non réglés",
    },
    {
      label: "Dépenses",
      to: `/agence/depenses?proprietaire=${proprietaire.id}`,
      icon: "ri-money-cny-circle-line",
      hint: "Charges du patrimoine",
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/agence/proprietaires"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux propriétaires
      </Link>

      <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span
              className={
                actif
                  ? "flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-semibold text-primary-700"
                  : "flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-background-100 font-heading text-lg font-semibold text-foreground-500"
              }
            >
              {initials || "?"}
            </span>
            <div className="min-w-0">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                {proprietaireNomComplet(proprietaire)}
              </h2>
              <div className="mt-2">
                <Badge
                  tone={actif ? "secondary" : "neutral"}
                  icon={actif ? "ri-checkbox-circle-line" : "ri-pause-circle-line"}
                >
                  {actif ? "Actif" : "Inactif"}
                </Badge>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Link
              to={`/agence/immeubles?proprietaire=${proprietaire.id}&nouveau=1`}
              className="inline-flex w-fit items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un immeuble
            </Link>
            <Link
              to="/agence/proprietaires"
              className="inline-flex w-fit items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-list-check" aria-hidden="true" />
              Tous les propriétaires
            </Link>
            {can("proprietaires.modifier") && (
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="inline-flex w-fit items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-edit-line text-base" aria-hidden="true" />
                Modifier
              </button>
            )}
            {can("proprietaires.supprimer") && (
              <button
                type="button"
                onClick={() => setToDelete(true)}
                className="inline-flex w-fit items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
              >
                <i className="ri-delete-bin-line text-base" aria-hidden="true" />
                Supprimer
              </button>
            )}
            <WhatsappButton numero={proprietaire.whatsapp} />
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-building-2-line" label="Immeubles" value={String(summary.counts.immeubles)} tone="primary" />
        <StatCard icon="ri-home-4-line" label="Biens" value={String(summary.counts.biens)} tone="secondary" />
        <StatCard icon="ri-user-3-line" label="Locataires" value={String(summary.counts.locataires)} tone="accent" />
        <StatCard icon="ri-file-text-line" label="Contrats" value={String(summary.counts.contrats)} tone="neutral" />
        <StatCard
          icon="ri-wallet-3-line"
          label="Loyers"
          value={formatFcfa(summary.loyers.montantTotal, { compact: true })}
          hint={`${summary.loyers.count} échéance${summary.loyers.count > 1 ? "s" : ""}`}
          tone="secondary"
        />
        <StatCard
          icon="ri-hand-coin-line"
          label="Paiements"
          value={formatFcfa(summary.paiements.montantTotal, { compact: true })}
          hint={`${summary.paiements.count} encaissement${summary.paiements.count > 1 ? "s" : ""}`}
          tone="primary"
        />
        <StatCard
          icon="ri-alert-line"
          label="Impayés"
          value={formatFcfa(summary.impayes.montantTotal, { compact: true })}
          hint={`${summary.impayes.count} échéance${summary.impayes.count > 1 ? "s" : ""} en retard`}
          tone="accent"
        />
        <StatCard
          icon="ri-building-2-line"
          label="Immeubles rattachés"
          value={String(summary.immeubles.length)}
          tone="neutral"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-information-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Informations du propriétaire
            </h3>
          </header>
          <div className="p-4 md:p-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={`${info.icon} text-base`} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            {proprietaire.notes && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">Notes</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{proprietaire.notes}</p>
              </div>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-links-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Accès rapides</h3>
          </header>
          <div className="flex flex-col gap-1 p-2">
            {accessModules.map((module) => (
              <Link
                key={module.to}
                to={module.to}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={`${module.icon} text-base`} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-label font-medium text-foreground-950">
                    {module.label}
                  </span>
                  <span className="block truncate text-xs text-foreground-500">{module.hint}</span>
                </span>
                <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className="ri-building-2-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Ses immeubles</h3>
            </span>
            <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
              {summary.immeubles.length}
            </span>
          </header>
          <div className="p-4 md:p-5">
            {summary.immeubles.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-primary-600">
                  <i className="ri-building-2-line text-xl" aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucun immeuble rattaché</p>
                <p className="mt-1 max-w-xs text-xs text-foreground-600">
                  Les immeubles créés par l'agence et rattachés à ce propriétaire apparaîtront ici.
                </p>
                <Link
                  to={`/agence/immeubles?proprietaire=${proprietaire.id}&nouveau=1`}
                  className="mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-add-line text-base" aria-hidden="true" />
                  Ajouter un immeuble
                </Link>
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200">
                {summary.immeubles.map((immeuble) => (
                  <li key={immeuble.id}>
                    <Link
                      to={`/agence/immeubles/${immeuble.id}`}
                      className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-label font-medium text-foreground-950">{immeuble.nom}</p>
                        <p className="mt-0.5 truncate text-xs text-foreground-500">
                          {[immeuble.commune, immeuble.province].filter(Boolean).join(", ")}
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
                <i className="ri-home-4-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Ses biens</h3>
            </span>
            <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
              {summary.biens.length}
            </span>
          </header>
          <div className="p-4 md:p-5">
            {summary.biens.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-secondary-600">
                  <i className="ri-home-4-line text-xl" aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucun bien rattaché</p>
                <p className="mt-1 max-w-xs text-xs text-foreground-600">
                  Les biens (appartements, boutiques, studios...) de ses immeubles apparaîtront ici.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200">
                {summary.biens.map((bien) => (
                  <li key={bien.id}>
                    <Link
                      to={`/agence/biens/${bien.id}`}
                      className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-label font-medium text-foreground-950">
                          {bien.reference || "Bien"}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-foreground-500">
                          {getImmeuble(bien.immeubleId)?.nom || "Immeuble non renseigné"} ·{" "}
                          {formatFcfa(bien.loyerMensuel)}
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
                <i className="ri-user-3-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Ses locataires</h3>
            </span>
            <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
              {summary.locataires.length}
            </span>
          </header>
          <div className="p-4 md:p-5">
            {summary.locataires.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-accent-700">
                  <i className="ri-user-3-line text-xl" aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucun locataire</p>
                <p className="mt-1 max-w-xs text-xs text-foreground-600">
                  Les locataires apparaissent ici dès qu'un contrat actif les relie à l'un de ses biens.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200">
                {summary.locataires.map((locataire) => (
                  <li key={locataire.id}>
                    <Link
                      to={`/agence/locataires/${locataire.id}`}
                      className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 cursor-pointer"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 font-heading text-xs font-semibold text-primary-700">
                          {agenceLocataireInitiales(locataire)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-label font-medium text-foreground-950">
                            {agenceLocataireNomComplet(locataire)}
                          </p>
                          <p className="mt-0.5 truncate text-xs text-foreground-500">
                            {locataire.telephone || "Téléphone non renseigné"}
                          </p>
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className="ri-file-text-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Ses contrats</h3>
            </span>
            <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
              {summary.contrats.length}
            </span>
          </header>
          <div className="p-4 md:p-5">
            {summary.contrats.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-primary-600">
                  <i className="ri-file-text-line text-xl" aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucun contrat</p>
                <p className="mt-1 max-w-xs text-xs text-foreground-600">
                  Les baux liés aux biens de ses immeubles apparaîtront ici.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200">
                {summary.contrats.map((contrat) => (
                  <li key={contrat.id}>
                    <Link
                      to={`/agence/contrats/${contrat.id}`}
                      className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-label font-medium text-foreground-950">
                          {agenceContratReference(contrat.id)} ·{" "}
                          {getBien(contrat.bienId)?.reference || "Bien"}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-foreground-500">
                          {getImmeuble(contrat.immeubleId)?.nom || "Immeuble non renseigné"} ·{" "}
                          {formatFcfa(contrat.loyerMensuel)} / mois
                        </p>
                      </div>
                      <Badge tone={AGENCE_CONTRAT_STATUT_TONES[contrat.statut]}>
                        {AGENCE_CONTRAT_STATUT_LABELS[contrat.statut]}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <ProprietaireForm open={editOpen} initial={proprietaire} onClose={() => setEditOpen(false)} />

      <ConfirmDeleteModal
        open={toDelete}
        onClose={() => setToDelete(false)}
        onConfirm={() => {
          removeProprietaire(proprietaire.id);
          navigate("/agence/proprietaires");
        }}
        title="Supprimer le propriétaire"
        description={`Voulez-vous vraiment supprimer « ${proprietaireNomComplet(proprietaire)} » ? Cette action est définitive.`}
        blockedMessage={
          immeublesByProprietaire(proprietaire.id).length > 0
            ? `Ce propriétaire possède ${immeublesByProprietaire(proprietaire.id).length} immeuble(s) rattaché(s). Supprimez ou réattribuez d'abord ses immeubles.`
            : undefined
        }
      />
    </div>
  );
}